import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { MentionHit, MentionType } from "../api/types";

// 打 `@` 之後跳出來的那張清單。
//
// 業主 2026-09-19:「跳出要TAG的類別/項目/人員 …… 要人性化 好選
//                一目了然怎麼選(不要加太多說明)」
//
// **一張混合清單,不是多層下拉。** 人記得的是名字(「介壽堂」),不是它屬於
// 哪一類 —— 層層選單逼使用者先回答一個他不在意的問題。類別寫在每一列右邊
// 當標籤,下面一排篩選鈕給「我就是知道類別」的人用,但它不擋路。
//
// 畫面上一個字的說明都沒有(業主:「不要加太多說明」)——
// 這張清單本身要能不解自明。

/** 每一類前面那個圖示。**一眼就看得出是哪一類**,不必讀字。 */
const ICON: Record<MentionType, string> = {
  QUOTE: "📄",
  COMPANY: "🏢",
  ITEM: "📦",
  USER: "👤",
  NOTE: "📝",
};

/** 篩選鈕。順序與後端 `MENTION_ORDER` 一致。 */
const FILTERS: [MentionType | "", string][] = [
  ["", "全部"],
  ["QUOTE", "報價單"],
  ["COMPANY", "客戶"],
  ["ITEM", "品項"],
  ["USER", "同仁"],
  ["NOTE", "會議"],
];

export interface MentionListHandle {
  /** 鍵盤事件。回 `true` 代表這張清單吃掉了它,編輯器不要再處理。 */
  onKeyDown: (event: KeyboardEvent) => boolean;
}

export interface MentionListProps {
  items: MentionHit[];
  /** 目前的類別篩選。`""` 是全部。 */
  filter: MentionType | "";
  onFilter: (next: MentionType | "") => void;
  command: (hit: MentionHit) => void;
}

export const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  function MentionList({ items, filter, onFilter, command }, ref) {
    const [at, setAt] = useState(0);
    // **鍵盤事件讀的是 ref,不是 state。**
    //
    // `onKeyDown` 是透過 `useImperativeHandle` 曝出去的,它的閉包抓到的是
    // 上一次算繪時的 `at`。連按兩個鍵而中間還沒重繪的時候(React 會批次),
    // 第二個鍵會用到舊的值 —— 按了 ↓ 再按 Enter 卻選到第一筆。
    const cursor = useRef(0);

    const move = (next: number) => {
      cursor.current = next;
      setAt(next);
    };

    // 清單換了就把游標移回第一筆 —— 停在第七筆而清單只剩兩筆會選到空的。
    useEffect(() => move(0), [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === "ArrowDown") {
          move(items.length ? (cursor.current + 1) % items.length : 0);
          return true;
        }
        if (event.key === "ArrowUp") {
          move(items.length ? (cursor.current - 1 + items.length) % items.length : 0);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          const picked = items[cursor.current];
          if (picked) command(picked);
          // **沒有東西可選的時候不要吃掉 Enter** —— 使用者要換行。
          return Boolean(picked);
        }
        return false;
      },
    }), [items, command]);

    return (
      <div className="w-80 overflow-hidden rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] shadow-lg">
        <ul role="listbox" aria-label="要提及什麼" className="m-0 max-h-64 list-none overflow-y-auto p-0">
          {items.map((one, index) => (
            <li key={`${one.type}-${one.id}`} role="option" aria-selected={index === at}>
              <button
                type="button"
                // `onMouseDown` 擋掉預設行為:不擋的話編輯器會先失去焦點,
                // 提及就插不進去了。
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => command(one)}
                onMouseEnter={() => move(index)}
                className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm ${
                  index === at ? "bg-[var(--surface-2)]" : ""
                }`}
              >
                <span aria-hidden="true">{ICON[one.type]}</span>
                <span className="min-w-0 flex-1 truncate text-[var(--text)]">{one.label}</span>
                <span className="shrink-0 text-xs text-[var(--text-3)]">{one.type_label}</span>
              </button>
            </li>
          ))}
          {items.length === 0 ? (
            <li className="px-2.5 py-2 text-sm text-[var(--text-3)]">沒有符合的</li>
          ) : null}
        </ul>

        {/* 第二層:知道類別的人點一下就縮小。**不點也選得到東西。** */}
        <div className="flex flex-wrap gap-1 border-t border-[var(--border)] bg-[var(--surface-2)] px-1.5 py-1">
          {FILTERS.map(([value, label]) => (
            <button
              key={label}
              type="button"
              aria-pressed={filter === value}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onFilter(value)}
              className={`rounded-full px-2 py-0.5 text-xs ${
                filter === value
                  ? "bg-[var(--accent)] text-[var(--on-accent)]"
                  : "text-[var(--text-2)] hover:bg-[var(--surface)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    );
  },
);
